import React, { RefObject } from 'react'
import type { FC } from 'react'
import RichText from '@/components/rich-text'
import { Media } from '@/components/media'
import { AdBlock } from '@/payload/blocks/ad-block/component'
import { PostHeader } from './post-header'
import { PostAuthors } from './post-authors'
import { RelatedPosts } from '@/payload/blocks/related-posts/component'
import { PostSidebar } from './post-sidebar'
import { Badge } from './badge'
import { ShareButtons } from './share-buttons'

import type { Post } from '@payload-types'
import { Separator } from '../ui/separator'

interface FullPostProps {
  post: Post
  randomPosts: Post[]
  latestCategoryPosts: Post[]
  isLast?: boolean
  scrollRef?: RefObject<HTMLDivElement | null>
}

export const FullPost: FC<FullPostProps> = ({
  post,
  randomPosts,
  latestCategoryPosts,
  isLast = false,
  scrollRef,
}) => {
  if (!post) return null

  // Get URL for Pinterest sharing
  const heroImageUrl =
    typeof post.heroImage === 'object' && post.heroImage?.url ? post.heroImage.url : ''

  // Construct the full URL for sharing
  const baseUrl = process.env.NEXT_PUBLIC_SERVER_URL || 'http://localhost:3001'
  const postSlug = post.slug || ''
  const postId = post.displayId?.toString() || ''
  const fullUrl = `${baseUrl}/${postSlug}/${postId}`

  return (
    <article className="container border-b border-slate-200">
      <PostHeader
        post={post}
        showViewCounter={true}
        initialViewCount={(post as any).views}
        uniqueViewCount={(post as any).uniqueIps ? Object.keys((post as any).uniqueIps).length : 0}
        showDetailedStats={false}
      />

      <div className="grid grid-cols-12 gap-4 pt-10">
        <div className="col-span-12 md:col-span-9">
          <Media resource={post.heroImage} alt={post.title || ''} />
          <Separator className="my-2" />

          <div className="flex justify-between items-center mx-auto w-full">
            {/* Social Share Buttons */}
            <ShareButtons
              slug={post.slug || ''}
              displayId={post.displayId?.toString() || ''}
              title={post.title || ''}
              heroImageUrl={
                typeof post.heroImage === 'object' && post.heroImage?.url ? post.heroImage.url : ''
              }
            />
            {/* Google News Follow Button */}
            <div className="flex items-center justify-end max-w-xs">
              <a
                href="https://news.google.com/publications/CAAqBwgKMKXFiwsw3ILrAg?hl=tr&gl=TR&ceid=TR%3Atr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-center gap-3 mx-auto">
                  <div className="flex items-center gap-2">
                    <img src="/images/google-news.webp" alt="Google News" className="w-6 h-6" />
                    <span className="text-sm font-medium">Google News</span>
                  </div>
                </div>
              </a>
            </div>
          </div>
          <Separator className="my-2" />
          <RichText data={post.content} enableGutter={false} className="mt-12 mb-10 md:mb-20" />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-row gap-2 flex-wrap mb-6">
              {post.tags.map((tag, index) => {
                if (typeof tag !== 'object' || !tag.title || !tag.slug) return null

                return (
                  <Badge
                    key={index}
                    title={`#${tag.title}`}
                    href={`/haberleri/${tag.slug}`}
                    variant="secondary"
                  />
                )
              })}
              {post.populatedAuthors && post.populatedAuthors.length > 0 && (
                <PostAuthors authors={post.populatedAuthors} />
              )}
            </div>
          )}

          {/* Article end marker - this is what we'll observe for infinite scrolling */}
          {isLast && (
            <div ref={scrollRef} className="w-full py-8 mb-8 text-center">
              <h3 className="text-xl font-semibold text-slate-800 mb-3">
                İlginizi Çekebilecek Haberlerimiz
              </h3>
              <p className="text-slate-600">Aşağıya kaydırmaya devam edin...</p>
            </div>
          )}
        </div>

        <PostSidebar latestPosts={randomPosts} />
      </div>
      {/* Single ad block */}
      {/*<AdBlock className="mb-5 md:mb-10" />*/}

      {latestCategoryPosts && latestCategoryPosts.length > 0 && (
        <RelatedPosts
          className="lg:grid lg:grid-cols-subgrid col-start-1 col-span-3 grid-rows-[2fr] mb-5 md:mb-10"
          docs={latestCategoryPosts.slice(0, 4)}
        />
      )}
    </article>
  )
}
